if (!customElements.get('wls-accordion')) {
  class WLSAccordion extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        accordionItem: 'wls-accordion-item',
        accordionTrigger: 'wls-accordion-trigger',
        accordionContent: 'wls-accordion-content',
      };

      this.settings = {
        triggerMob: this.hasAttribute('data-trigger-mob'),
        triggerTab: this.hasAttribute('data-trigger-tab'),
        triggerDesk: this.hasAttribute('data-trigger-desk'),
        closeOthers: this.hasAttribute('data-close-others'),
      };

      this.boundToggleAccordion = this._toggleAccordion.bind(this);
      this.boundResizeHandler = debounce(this._handleResize.bind(this), 200);
      this.boundOpenItemHandler = this._handleOpenItem.bind(this);
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        accordionItems: this.querySelectorAll(this.classes.accordionItem),
      };
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      this._init();
      window.addEventListener('resize', this.boundResizeHandler);
      this.boundCloseAllHandler = this._closeAllAccordions.bind(this);
      this.addEventListener('wls:accordion:close-all', this.boundCloseAllHandler);
      this.addEventListener('wls:accordion:open-item', this.boundOpenItemHandler);
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      this._teardown();
      window.removeEventListener('resize', this.boundResizeHandler);
      this.removeEventListener('wls:accordion:close-all', this.boundCloseAllHandler);
      this.removeEventListener('wls:accordion:open-item', this.boundOpenItemHandler);
    }

    /**
     * Initialize accordion
     * @returns {void}
     */
    _init() {
      if (!this._shouldEnableAccordion()) return;

      const defaultIndex = parseInt(this.getAttribute('data-default-open-index'), 10);

      this.elements.accordionItems.forEach((item, index) => {
        const itemTrigger = item.querySelector(this.classes.accordionTrigger);
        if (itemTrigger) {
          itemTrigger.addEventListener('click', this.boundToggleAccordion);
        }
        const dataIndex = parseInt(item.getAttribute('data-index'), 10);

        if (!isNaN(defaultIndex) && (defaultIndex === index || defaultIndex === dataIndex)) {
          this._openAccordion(item);
        }
      });
    }

    /**
     * Teardown accordion by removing event listeners
     * @returns {void}
     */
    _teardown() {
      if (!this.elements || !this.elements.accordionItems) return;
      this.elements.accordionItems.forEach((item) => {
        const itemTrigger = item.querySelector(this.classes.accordionTrigger);
        if (itemTrigger) {
          itemTrigger.removeEventListener('click', this.boundToggleAccordion);
        }
      });
    }

    /**
     * Handle window resize event
     * @returns {void}
     */
    _handleResize() {
      this._teardown();
      this._init();
    }

    /**
     * Check if accordion should be enabled based on breakpoints
     * @returns {boolean} - Whether accordion should be enabled
     */
    _shouldEnableAccordion() {
      const width = window.innerWidth;
      const { triggerMob, triggerTab, triggerDesk } = this.settings;
      if (!triggerMob && !triggerTab && !triggerDesk) return true;
      if (triggerMob && width <= 767) return true;
      if (triggerTab && width > 767 && width <= 1023) return true;
      if (triggerDesk && width > 1023) return true;
      return false;
    }

    /**
     * Toggle accordion item open/closed
     * @param {Event} e - The click event
     * @returns {void}
     */
    _toggleAccordion(e) {
      const item = e.currentTarget.closest(this.classes.accordionItem);
      const isOpen = item.hasAttribute('data-expanded');
      const itemContent = item.querySelector(this.classes.accordionContent);
      const itemTrigger = item.querySelector(this.classes.accordionTrigger);

      if (!itemContent) return;

      if (isOpen) {
        itemContent.style.maxHeight = '0px';
        item.removeAttribute('data-expanded');
        if (itemTrigger) itemTrigger.setAttribute('aria-expanded', 'false');
      } else {
        if (this.settings.closeOthers) {
          this._closeAllAccordions();
        }
        this._openAccordion(item);
      }
    }

    /**
     * Open accordion item
     * @param {HTMLElement} item - The accordion item to open
     * @returns {void}
     */
    _openAccordion(item) {
      const itemContent = item.querySelector(this.classes.accordionContent);
      const itemTrigger = item.querySelector(this.classes.accordionTrigger);
      if (!itemContent) return;
      item.setAttribute('data-expanded', 'true');
      if (itemTrigger) itemTrigger.setAttribute('aria-expanded', 'true');
      itemContent.style.maxHeight = `${itemContent.scrollHeight}px`;
    }

    /**
     * Close all accordion items
     * @returns {void}
     */
    _closeAllAccordions() {
      if (!this.elements || !this.elements.accordionItems) return;
      this.elements.accordionItems.forEach((item) => {
        const itemContent = item.querySelector(this.classes.accordionContent);
        const itemTrigger = item.querySelector(this.classes.accordionTrigger);
        if (itemContent) itemContent.style.maxHeight = '0px';
        if (itemTrigger) itemTrigger.setAttribute('aria-expanded', 'false');
        item.removeAttribute('data-expanded');
      });
    }

    /**
     * Handle custom event to open specific accordion item by index
     * @param {CustomEvent} event - Custom event with detail.index
     * @returns {void}
     */
    _handleOpenItem(event) {
      if (!event.detail || !event.detail.index) return;

      const targetIndex = event.detail.index.toString();
      const targetItem = Array.from(this.elements.accordionItems).find((item) => {
        return item.getAttribute('data-index') === targetIndex;
      });

      if (targetItem) {
        // Close others if setting is enabled
        if (this.settings.closeOthers) {
          this._closeAllAccordions();
        }
        this._openAccordion(targetItem);
      }
    }
  }

  window.customElements.define('wls-accordion', WLSAccordion);
}
